import React, {
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from 'react';
import clsx from 'clsx';
import scrollIntoView from 'scroll-into-view-if-needed';
import _ from 'underscore';
import {type Row, type RowData} from '@tanstack/react-table';
import {
	defaultRangeExtractor,
	type Range,
	useVirtualizer,
	type Virtualizer,
} from '@tanstack/react-virtual';
import {type ColumnPinningState, type Table} from '@tanstack/table-core';
import {
	type ArrowFocusTrapContextType,
	ArrowFocusTrapProvider,
} from '@netx/core-components/ArrowFocusTrapNext/ArrowFocusTrapContext';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useDebouncedCallback} from '@netx/core-components/hooks/useDebouncedCallback';
import {useDocumentKeyDown} from '@netx/core-components/hooks/useDocumentKeyDown';
import {useInfiniteLoader} from '@netx/core-components/hooks/useInfiniteLoader';
import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {silenceEvent} from '@netx/core-next/utils';
import {getScrollparent} from '@netx/core-next/utils/element';
import {
	isItemLoadedStub,
	loadMoreItemsStub,
} from '@netx/core-next/utils/virtualization';
import {
	getTableBodyUtilityClass,
	TABLE_BODY_CLASS_NAME,
	tableBodyClasses,
	type TableBodyClasses,
} from './tableBodyClasses';
import {TableBodyRow, type TableBodyRowProps} from './TableBodyRow';
import {TableBodyRowLoading} from './TableBodyRowLoading';

type TableBodyOwnerState = WithClasses<TableBodyClasses>;

const useUtilityClasses = (ownerState: TableBodyOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getTableBodyUtilityClass, classes);
};

const TableBodyRoot = styled('div', {
	name: TABLE_BODY_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	zIndex: 2,
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	background: theme.palette.custom.background.light,
	'&.disabled': {
		...theme.mixins.disabled,
		pointerEvents: 'none',
	},
}));

export const TableBodyRowStyled = styled(TableBodyRow)(({theme}) => ({
	// height: 56,
	background: theme.palette.background.default,
	position: 'absolute',
	willChange: 'transform, top',
})) as typeof TableBodyRow;

export const TableBodyRowLoadingStyled = styled(TableBodyRowLoading)(
	({theme}) => ({
		background: theme.palette.background.default,
		height: 56,
		position: 'absolute',
		willChange: 'transform, top',
	}),
) as typeof TableBodyRowLoading;

const defaultRowRenderer = <TData extends RowData>(
	props: RenderRowProps<TData>,
) => {
	return <TableBodyRowStyled {...props} />;
};

export type RenderRowProps<TData extends RowData> = Pick<
	TableBodyRowProps<TData>,
	| 'ref'
	| 'row'
	| 'isEngaged'
	| 'index'
	| 'isActive'
	| 'lastSizeableColumnIndex'
	| 'leftLeafColumnWidths'
	| 'pinnedColumns'
	| 'progress'
	| 'rightLeafColumnWidths'
	| 'style'
	| 'virtualHeight'
	| 'flagRow'
	| 'onRowClick'
	| 'onContextMenu'
> & {
	'data-index'?: number;
	key: string;
	start: number;
};

export interface TableBodyProps<
	TData extends RowData,
> extends WithClasses<TableBodyClasses> {
	allowDynamicRowHeight?: boolean;
	/** A `ref` to the arrow focus trap instance */
	arrowFocusTrapRef?: React.RefObject<ArrowFocusTrapContextType | null>;
	minimumBatchSize?: number;
	disabled?: boolean;
	disabledArrowFocusTrap?: boolean;
	engagedRowId?: string | null;
	headerHeight?: number;
	/**
	 * The index of a row of data to hold onto (invisibly) in the DOM
	 *
	 * Useful in some cases where a row is being worked with and would otherwise unmount.
	 */
	holdRowIndex?: number;
	/** The index of the last sizeable column */
	lastSizeableColumnIndex?: number;
	/** The width of the left leaf (pinned) columns */
	leftLeafColumnWidths?: number[];
	/** The amount of items to overscan when virtualizing */
	overscan?: number;
	/**
	 * Pinned columns state
	 *
	 * @example
	 * ```tsx
	 * pinnedColumns={{
	 * 	left: ['column-1'],
	 * 	right: ['column-2'],
	 * }}
	 * ```
	 */
	pinnedColumns?: ColumnPinningState;
	/** The width of the right leaf (pinned) columns */
	rightLeafColumnWidths?: number[];
	/**
	 * The prefix for the active descendant id
	 *
	 * This is used for the arrow focus trap.
	 * @example
	 * ```tsx
	 * rowActiveDescendantPrefix="foo-bar" // would result in `foo-bar-0`, `foo-bar-1`, etc.
	 * ```
	 */
	rowActiveDescendantPrefix?: string;
	/** A `ref` to the row virtualizer instance */
	rowVirtualizerRef?: React.RefObject<Virtualizer<Element, Element>>;
	/** Table rows data */
	rows: Row<TData>[];
	/**
	 * Enforce a width on all rows
	 *
	 * Note: if this value is smaller than `stageWidth`, then `stageWidth` will be used
	 */
	rowWidth?: number;
	/** The initial scroll index on mount */
	scrollIndex?: number;
	/** The `ref` to the scroll parent element */
	scrollRef: React.RefObject<HTMLDivElement>;
	/**
	 * Force an active state on a row by its id
	 *
	 * Note: row ids are always strings even if the data id is not (e.g. `id: 1` would be `'1'`)
	 */
	showRowActive?: string;
	/**
	 * The width of the stage
	 *
	 * This is important to ensure the rows are at least the width of the stage.
	 */
	stageWidth: number;
	/** The amount of rows we want to render currently for the subset of data */
	subsetToShow?: number;
	/** Total row count that would represent the complete subset of data */
	subsetTotal?: number;
	/** Table instance */
	table: Table<TData>;
	/** Total row count when not using a subset of the data */
	totalRows: number;

	rowHeight?: number;

	/**
	 * Callback for checking if the row data is loaded.
	 * @param index The index of the item to check if it is loaded
	 * @returns
	 */
	isItemLoaded?: (index: number) => boolean;
	/**
	 * Callback for when more items need to be loaded.
	 * @param startIndex The index to start loading from
	 * @param stopIndex The index to stop loading at
	 * @returns A promise that resolves when the items have been loaded (or void)
	 */
	loadMoreItems?: (
		startIndex: number,
		stopIndex: number,
	) => Promise<unknown> | void;
	/**
	 * Allows for passing in a custom row renderer.
	 * @param props The props that would have been passed to `TableBodyRow`
	 * @returns JSX.Element
	 */
	renderRow?: (props: RenderRowProps<TData>) => React.ReactNode;
	/**
	 * Callback for when a row is clicked.
	 * This differs from a rows actual `onClick` event in that it deduce a click vs text selection
	 * @param row The row that was clicked
	 * @param modifiers An object containing the meta and shift key modifiers at time of click
	 */
	onRowClick?: (
		row: Row<TData>,
		modifiers: {meta: boolean; shift: boolean},
	) => void;
	/**
	 * Callback for when a row is right/context clicked.
	 * @param event The original mouse event
	 * @param row The row that was clicked
	 */
	onRowContextMenu?: (event: React.MouseEvent, row: Row<TData>) => void;
	/**
	 * Callback for when the range of visible rows changes.
	 * @param rangeStart The start index of the visible range
	 * @param rangeEnd The end index of the visible range
	 */
	onStartIndexUpdate?: (rangeStart: number, rangeEnd: number) => void;

	getRowProgress?: (row: Row<TData>) => number | undefined;
	getRowProps?: (
		row: Row<TData>,
		props: RenderRowProps<TData>,
	) => Partial<TableBodyRowProps<TData>> | undefined;
	flagRow?: (
		row: Row<TData>,
	) =>
		| 'info'
		| 'warning'
		| 'error'
		| 'success'
		| 'primary'
		| 'secondary'
		| undefined;
}

export const TableBodyPrimitive = <TData extends RowData>(
	{
		classes: _classes,
		className,
		allowDynamicRowHeight,
		arrowFocusTrapRef: _arrowFocusTrapRef,
		disabled,
		disabledArrowFocusTrap,
		engagedRowId,
		headerHeight = 56,
		holdRowIndex,
		lastSizeableColumnIndex,
		minimumBatchSize = 25,
		pinnedColumns,
		leftLeafColumnWidths,
		overscan = 10,
		rightLeafColumnWidths,
		rowHeight = 56,
		rowActiveDescendantPrefix = 'row',
		rowVirtualizerRef,
		rows,
		rowWidth: _rowWidth = 0,
		totalRows,
		scrollIndex = 0,
		scrollRef,
		showRowActive,
		stageWidth,
		subsetToShow,
		subsetTotal,
		table,

		flagRow,
		getRowProgress,
		getRowProps,
		loadMoreItems = loadMoreItemsStub,
		isItemLoaded = isItemLoadedStub,
		renderRow = defaultRowRenderer<TData>,
		onRowClick,
		onRowContextMenu,
		onStartIndexUpdate,
		...props
	}: TableBodyProps<TData>,
	ref: React.ForwardedRef<HTMLTableSectionElement>,
) => {
	const rootRef = useSharedRef<HTMLTableSectionElement | null>(null, [ref]);
	const arrowFocusTrapRef = useSharedRef(null, [_arrowFocusTrapRef]);

	const ownerState: TableBodyOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const scrollParentRef = useRef<Element | null>(null);
	useEffect(() => {
		if (scrollParentRef.current) return;
		scrollParentRef.current = getScrollparent(rootRef.current!);
	});

	const lastActiveIndex = useRef(-1);

	// this is for when the virtualized ranges update (we are scrolling through the tree)
	// if we do not update the focus trap to reflect that new visible items,
	// when you regain focus in the area it will take you back to the last active item
	// which would now be out of view.
	// we debounce this for obvious reasons: the is in relation to what likely came from a scroll event
	const debouncedSetActiveItemIndex = useDebouncedCallback((range: Range) => {
		const {endIndex, startIndex} = range;
		if (lastActiveIndex.current < startIndex) {
			arrowFocusTrapRef.current?.setActiveItemIndex(startIndex, true);
		} else if (lastActiveIndex.current > endIndex) {
			arrowFocusTrapRef.current?.setActiveItemIndex((endIndex || 1) - 1, true);
		}
	}, 100);

	// setup the virtualizer
	const rowVirtualizer = useVirtualizer({
		count: subsetToShow ?? totalRows,
		overscan,
		initialOffset: rowHeight * scrollIndex,
		estimateSize: () => rowHeight,
		getScrollElement: () => scrollParentRef.current,
		rangeExtractor: (range: Range) => {
			debouncedSetActiveItemIndex(range);
			return defaultRangeExtractor(range);
		},
	});

	// when the range changes
	useEffect(() => {
		// notify the parent of the range change
		onStartIndexUpdate?.(
			rowVirtualizer.range?.startIndex ?? 0,
			rowVirtualizer.range?.endIndex ?? 0,
		);
	}, [rowVirtualizer.range]);

	// expose the virtualizer instance
	useImperativeHandle(rowVirtualizerRef, () => rowVirtualizer, [
		rowVirtualizer,
	]);

	const pageKeydownTimeout = useRef<NodeJS.Timeout | null>(null);
	const wasPageKeydown = useRef(false);
	useDocumentKeyDown((event) => {
		switch (event.key) {
			case 'ArrowUp':
			case 'ArrowDown':
			case 'Home':
			case 'End':
			case 'PageUp':
			case 'PageDown':
				if (pageKeydownTimeout.current)
					clearTimeout(pageKeydownTimeout.current);
				wasPageKeydown.current = true;
				pageKeydownTimeout.current = setTimeout(() => {
					wasPageKeydown.current = false;
				}, 100);
				break;
		}
	});

	useEffect(() => {
		return () => {
			if (pageKeydownTimeout.current) clearTimeout(pageKeydownTimeout.current);
		};
	}, []);

	// get the virtual rows
	const virtualRows = rowVirtualizer.getVirtualItems();
	// get the total size of the rows
	const totalSize = rowVirtualizer.getTotalSize();
	// get all the visible columns
	const columns = table.getVisibleLeafColumns();

	// ensure the row width is at least the stage width
	const rowWidth = Math.max(_rowWidth, stageWidth);

	const lastUpdateRef = useRef(0);
	useEffect(() => {
		if (allowDynamicRowHeight) {
			lastUpdateRef.current = Date.now();
			rowVirtualizer.measure();
		}
	}, [rows, allowDynamicRowHeight]);

	// handle row click
	// this is just a wrapper to provide the row data and modifiers
	const handleRowClick = useConstCallback((event: React.MouseEvent) => {
		const rowIndex = parseInt(
			event.currentTarget.getAttribute('data-index') ?? '-1',
			10,
		);
		if (rowIndex < 0 || rowIndex >= rows.length) return;
		const row = rows[rowIndex];
		if (!row) return;
		onRowClick?.(row, {
			meta: event.metaKey,
			shift: event.shiftKey,
		});
	});

	// handle row context menu
	const handleRowContextMenu = useConstCallback((event: React.MouseEvent) => {
		const rowIndex = parseInt(
			event.currentTarget.getAttribute('data-index') ?? '-1',
			10,
		);
		if (rowIndex < 0 || rowIndex >= rows.length) return;
		const row = rows[rowIndex];
		event.preventDefault();
		event.stopPropagation();
		if (!row) return;
		// set the active index to the row that was right clicked
		// arrowFocusTrapRef.current?.setActiveIndex(row.index);
		// call the onRowContextMenu callback
		onRowContextMenu?.(event, row);
	});

	const handlRowRef = useCallback(
		(node: HTMLElement | null) => {
			return rowVirtualizer.measureElement(node);
		},
		[rows],
	);

	// setup the infinite loader
	useInfiniteLoader({
		data: rows,
		itemCount: subsetToShow ?? totalRows,
		minimumBatchSize,
		overscan,
		startIndex: rowVirtualizer.range?.startIndex || 0,
		stopIndex: rowVirtualizer.range?.endIndex || 0,
		isItemLoaded,
		loadMoreItems,
	});

	const commonStyles = useMemo(() => {
		return {
			width: rowWidth,
			height: allowDynamicRowHeight ? undefined : rowHeight,
			minHeight: allowDynamicRowHeight ? rowHeight : undefined,
		};
	}, [rowWidth, allowDynamicRowHeight, rowHeight]);

	// the total that will be used for the arrow focus trap
	const arrowFocusTrapRowTotal = subsetTotal ?? totalRows;
	return (
		<ArrowFocusTrapProvider
			ref={arrowFocusTrapRef}
			disabled={disabled || disabledArrowFocusTrap || engagedRowId != null}
			initialActiveItemIndex={scrollIndex}
			itemsCount={arrowFocusTrapRowTotal}
			onChangeActiveItemIndex={(index) => {
				// console.log('active item index', index);
				lastActiveIndex.current = index;
			}}
			isActiveItemIndexInView={(index) => {
				// rowVirtualizer.measureElement?.(undefined);
				return (
					index >= (rowVirtualizer.range?.startIndex ?? 0) + 1 &&
					index <= (rowVirtualizer.range?.endIndex ?? 0) - 1
				);
			}}
			scrollToItemIndex={(index) => {
				const direction = lastActiveIndex.current > index ? 0 : 1;
				if (index < 0 || index > arrowFocusTrapRowTotal) return true;

				if (wasPageKeydown.current) {
					const normalizedIndex = Math.min(
						direction + index,
						arrowFocusTrapRowTotal - 1,
					);
					const offset = rowVirtualizer.getOffsetForIndex(normalizedIndex);
					if (direction) {
						offset != null &&
							scrollParentRef.current?.scrollTo({
								top: offset[0] + headerHeight,
							});
					} else {
						rowVirtualizer.scrollToIndex(normalizedIndex, {
							behavior: 'auto',
							align: index === arrowFocusTrapRowTotal - 1 ? 'start' : 'auto',
						});
					}
				} else {
					rowVirtualizer.scrollToIndex(index, {align: 'auto'});
				}
			}}
			onItemKeyClick={
				onRowClick
					? (event, index) => {
							if (
								event.defaultPrevented ||
								(event.target as HTMLElement).getAttribute(
									'data-ignore-enter-key',
								) != null
							)
								return;
							silenceEvent(event);
							onRowClick(rows[index], {
								meta: event.metaKey,
								shift: event.shiftKey,
							});
						}
					: undefined
			}
		>
			{({
				activeDescendantValue,
				disabled: focusTrapDisabled,
				activeItemIndex,
				isFocusTrapActive,
				handleContainerBlur,
				handleContainerFocus,
			}) => (
				// <ArrowFocusTrapContext
				// 	disabled={disabled || disabledArrowFocusTrap || engagedRowId != null}
				// 	restoreFocusOnEnable={engagedRowId != null}
				// 	ref={arrowFocusTrapRef}
				// 	initialActiveIndex={scrollIndex}
				// 	activeDescendantLabel={rowActiveDescendantPrefix}
				// 	total={arrowFocusTrapRowTotal}
				// 	onItemKeyDown={(event, index) => {
				// 		if (
				// 			!onRowClick ||
				// 			event.defaultPrevented ||
				// 			(event.target as HTMLElement).getAttribute('data-ignore-enter-key') !=
				// 				null
				// 		)
				// 			return;

				// 		// if the key is not one of
				// 		switch (event.key) {
				// 			case 'Enter':
				// 			case ' ':
				// 				silenceEvent(event);
				// 				onRowClick(rows[index], {
				// 					meta: event.metaKey,
				// 					shift: event.shiftKey,
				// 				});
				// 				return;
				// 		}
				// 	}}
				// 	onActiveIndexChange={(index) => {
				// 		const direction = lastActiveIndex.current > index ? 0 : 1;
				// 		if (index < 0 || index > arrowFocusTrapRowTotal) return;
				// 		// TODO: we are doing this for the arrow keys since sticky position headers
				// 		// still have some quirks in browsers
				// 		// this is to make sure the row is actually visible
				// 		if (wasPageKeydown.current) {
				// 			const normalizedIndex = Math.min(
				// 				direction + index,
				// 				arrowFocusTrapRowTotal - 1,
				// 			);
				// 			const offset = rowVirtualizer.getOffsetForIndex(normalizedIndex);
				// 			if (direction) {
				// 				offset != null &&
				// 					scrollParentRef.current?.scrollTo({
				// 						top: offset[0] + headerHeight,
				// 					});
				// 			} else {
				// 				rowVirtualizer.scrollToIndex(normalizedIndex, {
				// 					behavior: 'auto',
				// 					align: index === arrowFocusTrapRowTotal - 1 ? 'start' : 'auto',
				// 				});
				// 			}
				// 		}
				// 		lastActiveIndex.current = index;
				// 	}}
				// >
				// 	{({activeIndex, disabled: arrowFocusTrapDisabled, userDisabled}) => {
				// return (
				<TableBodyRoot
					role="rowgroup"
					{...props}
					ref={rootRef}
					className={clsx(classes.root, className, {
						disabled,
					})}
					aria-activedescendant={disabled ? undefined : activeDescendantValue}
					tabIndex={isFocusTrapActive ? -1 : 0}
					onBlur={handleContainerBlur}
					onFocus={handleContainerFocus}
					style={{width: rowWidth, height: totalSize}}
				>
					{virtualRows.map((virtualProps) => {
						if (!virtualProps) return null;
						const {index, start, size} = virtualProps;
						// despite the typing here, the row data may not be available
						const row = rows[index] as Row<TData> | undefined;
						const isEngaged = engagedRowId === row?.id;

						const commonProps = {
							'aria-rowindex': index + 1,
							id: `${rowActiveDescendantPrefix}-${index}`,
							disabled: engagedRowId != null && !isEngaged,
							index,
							isActive:
								!isEngaged &&
								((isFocusTrapActive &&
									!focusTrapDisabled &&
									activeItemIndex === index) ||
									(row && showRowActive === row.id)),
							lastSizeableColumnIndex,
							leftLeafColumnWidths,
							pinnedColumns,
							rightLeafColumnWidths,
							onContextMenu: handleRowContextMenu,
						};

						// show loading
						if (row == null || row.original == null)
							return (
								<TableBodyRowLoadingStyled<TData>
									key={`loading-${index}`}
									{...commonProps}
									columns={columns}
									style={{
										...commonStyles,
										transform: `translateY(${start}px)`,
									}}
								/>
							);

						const baseRowProps: RenderRowProps<TData> = {
							...commonProps,
							key: `${row.id}-${lastUpdateRef.current}`,
							['data-index']: index,
							isEngaged,
							ref: handlRowRef,
							flagRow,
							progress: getRowProgress?.(row),
							row,
							start,
							virtualHeight: size,
							style: {
								...commonStyles,
								transform: `translateY(${start}px)`,
							},
							// only add the click handler if it is defined from the outside
							onRowClick: onRowClick ? handleRowClick : undefined,
						};

						const rowProps = getRowProps?.(row, baseRowProps);

						return renderRow({
							...baseRowProps,
							...rowProps,
							key: `${row.id}-${lastUpdateRef.current}`,
						});
					})}
					{/** TODO: better naming / cleanup - this is for drag sorting with the keyboard */}
					{/** another option maybe is to disable the drag in the row that is not the overlay... */}
					{/** can it just be handled in that body component? */}
					{holdRowIndex != null &&
						holdRowIndex > -1 &&
						(holdRowIndex > (rowVirtualizer.range?.endIndex ?? 0) ||
							holdRowIndex < (rowVirtualizer.range?.startIndex ?? 0)) &&
						rows[holdRowIndex] != null &&
						renderRow({
							key: 'hold-row',
							index: holdRowIndex,
							row: rows[holdRowIndex],
							start: 0,
							style: {
								visibility: 'hidden',
								pointerEvents: 'none',
							},
						})}
				</TableBodyRoot>
			)}
		</ArrowFocusTrapProvider>
	);
};

/**
 * The `TableBody` component is a container for the rows of a table.
 *
 * It is responsible for rendering the rows and managing the virtualization of the rows.
 *
 * It also manages the arrow focus trap for keyboard navigation.
 *
 * @typeParam TData The type of data that represents a row
 *
 * @example
 * ```tsx
 * <TableBody
 * 	rows={rows}
 * 	scrollRef={scrollRef}
 * 	stageWidth={stageWidth}
 * 	table={table}
 * 	totalRows={totalRows}
 * />
 * ```
 */
export const TableBody = React.memo(React.forwardRef(TableBodyPrimitive)) as <
	TData extends RowData,
>(
	props: TableBodyProps<TData> & {ref?: React.ForwardedRef<HTMLDivElement>},
) => JSX.Element;

export default TableBody;
